import { t } from "@lingui/macro";
import { Select, Spin } from "antd";
import { useEffect, useState, useRef } from "react";
import { ApiResponse } from "types";
import { BaseQuery } from "service/queries";
import { debounce } from "lodash";
import { translate } from "utils";

const { Option } = Select;

const ERROR_MARGIN_SIZE = 3;

export interface QueryInfinityProps extends BaseQuery {
	total?: number | null;
}
export interface SelectOptionsProps {
	query: QueryInfinityProps;
	setQuery: Function;
	renderList: any[];
	setRenderList: Function;
	fetchMoreFunc?: Function;
	onSearch?: (event: string) => void;
	onChange?: (event: string) => void;
	value?: string;
	className?: string;
	placeholder?: string;
	showSearch?: boolean;
	haveAllValue?: boolean;
	optionRender: Function;
	disabled?: boolean;
	isNotLoadingOnSearch?: boolean;
	allowClear?: boolean;
	handleOnClear?: () => void;
	allValueText?: string;
}

const SelectInfinity = ({
	query,
	setQuery,
	renderList = [],
	setRenderList,
	fetchMoreFunc,
	onSearch,
	onChange,
	showSearch,
	haveAllValue,
	optionRender,
	value,
	placeholder,
	disabled,
	isNotLoadingOnSearch,
	allowClear,
	handleOnClear,
	allValueText = "All",
	...props
}: SelectOptionsProps) => {
	const [isLoading, setIsLoading] = useState(false);
	const [searchValue, setSearchValue] = useState("");

	useEffect(() => {
		const { skip, total = 0 } = query;
		setIsLoading(true);
		//Call api first time to get total
		if (!skip) {
			if (fetchMoreFunc) {
				fetchMoreFunc(query).then((result: ApiResponse<any[]>) => {
					setQuery((prev: QueryInfinityProps) => ({
						...prev,
						total: result.total,
					}));
					setRenderList([...result.data]);
					setIsLoading(false);
				});
			} else setIsLoading(false);
		}
		// If still have data to get
		else if (skip * 10 < total!) {
			if (fetchMoreFunc) {
				fetchMoreFunc(query).then((result: ApiResponse<any[]>) => {
					setRenderList([...renderList, ...result.data]);
					setIsLoading(false);
				});
			} else setIsLoading(false);
		} else setIsLoading(false);
	}, [query.skip, searchValue, value]);

	const onScroll = (event: { target: any }) => {
		const target = event.target;
		const { skip, total = 0 } = query;

		if (
			!isLoading &&
			target.scrollTop + target.offsetHeight >=
				target.scrollHeight - ERROR_MARGIN_SIZE &&
			skip * 10 < total!
		) {
			setIsLoading(true);
			setQuery((prev: QueryInfinityProps) => ({
				...prev,
				skip: prev.skip + 1,
			}));
		}
	};

	const debounceSetSearch = useRef(
		debounce((searchValue: string) => {
			setSearchValue(searchValue);
			onSearch && onSearch(searchValue);
		}, 500),
	).current;

	const handleOnSearch = (searchValue: string) => {
		if (!isNotLoadingOnSearch) {
			setIsLoading(true);
			debounceSetSearch(searchValue);
		}
	};

	return (
		<Select
			loading={isLoading}
			onChange={onChange}
			onSearch={handleOnSearch}
			onPopupScroll={onScroll}
			value={value}
			placeholder={placeholder}
			disabled={disabled}
			{...props}
			showSearch={showSearch}
			allowClear={allowClear}
			onClear={handleOnClear}
		>
			{haveAllValue && <Option value={""}>{translate(allValueText)}</Option>}
			{renderList.map((item: any, index: number) =>
				optionRender ? (
					optionRender(item, index)
				) : (
					<Option value={item.value} key={index}>
						<div>{item.fullName}</div>
					</Option>
				),
			)}
			{isLoading && (
				<div className="text-sky-600 text-center">
					<Spin size="small" />
					<span className="ml-2">Loading...</span>
				</div>
			)}
		</Select>
	);
};

export default SelectInfinity;
